import {
  Fragment,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
  KeyboardEvent,
} from "react";
import { IViewerApi } from "@bronte/viewer-api/src/main";

import {
  bootstrapSearchContext,
  fetchGlossary,
  fetchSearchResults,
  fetchSearchSuggestions,
  filterGlossary,
} from "../../shared/searchService";
import {
  navigateToGlossaryTerm,
  navigateToSearchResult,
  SearchNavigateTarget,
} from "../../shared/searchNavigation";
import {
  AutoCompleteResponse,
  CsgGlossaryItem,
  SearchContext,
  SearchFacetGroup,
  SearchProduct,
} from "../../shared/searchTypes";
import "./styles.scss";

interface ISearchDrawer {
  viewerApi: IViewerApi;
  packagePath?: string;
  onClose: () => void;
}

const SUGGEST_DEBOUNCE_MS = 300;
const MIN_SUGGEST_LENGTH = 2;
/** Internal key so the glossary group never collides with a CSG facet named "glossary". */
const GLOSSARY_GROUP_KEY = "__glossary__";
/** autoComplete glossary facet; superseded by the CSG glossary API results. */
const GLOSSARY_FACET_KEY = "glossary";

function renderHighlightedHtml(html: string): ReactElement {
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

function getProductTitle(product: SearchProduct): string | undefined {
  const { source, matchedFields } = product;
  if (matchedFields?.pageTitle) {
    return matchedFields.pageTitle;
  }
  if (matchedFields?.chapterTitle) {
    return matchedFields.chapterTitle;
  }
  if (matchedFields?.title) {
    return matchedFields.title;
  }
  if (source.pageTitle) {
    return source.pageTitle;
  }
  if (source.chapterTitle) {
    return source.chapterTitle;
  }
  if (source.title) {
    return source.label ? `${source.label}: ${source.title}` : source.title;
  }
  return undefined;
}

function getProductSnippets(product: SearchProduct): string[] {
  const snippets: string[] = [];
  const arrays = product.matchedFieldArray;
  if (arrays?.content?.length) {
    snippets.push(...arrays.content);
  } else if (arrays?.definition?.length) {
    snippets.push(...arrays.definition);
  } else if (arrays?.caption?.length) {
    snippets.push(...arrays.caption);
  } else if (product.matchedFields?.content) {
    snippets.push(product.matchedFields.content);
  } else if (product.matchedFields?.definition) {
    snippets.push(product.matchedFields.definition);
  } else if (product.matchedFields?.term) {
    snippets.push(product.matchedFields.term);
  }
  return snippets;
}

function getChapterGroupKey(product: SearchProduct): string {
  const title = product.source.pageTitle || product.source.chapterTitle || getProductTitle(product);
  if (!title) {
    return "Other";
  }
  const chapterMatch = /^(\d+):/.exec(title);
  if (chapterMatch) {
    return title.split(":")[0] + ":" + (title.split(":")[1]?.trim().split(/[.:]/)[0] ?? "");
  }
  const sectionMatch = /^(\d+\.\d+):/.exec(title);
  if (sectionMatch) {
    const chapterNum = title.split(".")[0];
    return `Chapter ${chapterNum}`;
  }
  return title.split(":")[0] || title;
}

interface GroupedResults {
  key: string;
  count: number;
  products: SearchProduct[];
}

function isGlossaryFacet(facet: SearchFacetGroup): boolean {
  return facet.key?.toLowerCase() === GLOSSARY_FACET_KEY;
}

function isGlossaryProduct(product: SearchProduct): boolean {
  return product.source.type?.toLowerCase() === GLOSSARY_FACET_KEY;
}

function groupResultsForDisplay(response: AutoCompleteResponse): GroupedResults[] {
  const pageProducts: SearchProduct[] = [];
  const otherProducts: SearchProduct[] = [];
  const facets = response.searchResults.filter((facet) => !isGlossaryFacet(facet));

  for (const facet of facets) {
    for (const product of facet.productsList) {
      if (isGlossaryProduct(product)) {
        continue;
      }
      if (facet.key === "page" || product.source.type === "page") {
        pageProducts.push(product);
      } else {
        otherProducts.push(product);
      }
    }
  }

  const grouped = new Map<string, SearchProduct[]>();

  for (const product of pageProducts) {
    const key = getChapterGroupKey(product);
    const list = grouped.get(key) ?? [];
    list.push(product);
    grouped.set(key, list);
  }

  const result: GroupedResults[] = Array.from(grouped.entries()).map(([key, products]) => ({
    key,
    count: products.length,
    products,
  }));

  if (otherProducts.length) {
    const byFacet = new Map<string, SearchProduct[]>();
    for (const facet of facets) {
      if (facet.key === "page") {
        continue;
      }
      const facetProducts = facet.productsList.filter(
        (p) => p.source.type !== "page" && !isGlossaryProduct(p),
      );
      if (facetProducts.length) {
        byFacet.set(facet.key, facetProducts);
      }
    }

    for (const [facetKey, products] of byFacet.entries()) {
      result.push({
        key: facetKey.charAt(0).toUpperCase() + facetKey.slice(1),
        count: products.length,
        products,
      });
    }
  }

  return result;
}

function SearchResultItem({
  product,
  selected,
  onNavigate,
}: {
  product: SearchProduct;
  selected: boolean;
  onNavigate: (target: SearchNavigateTarget) => void;
}): ReactElement {
  const pageUrn = product.source.pageUrn || product.source.pageUrl;
  const dataId = product.source.id;
  const title = getProductTitle(product);
  const snippets = getProductSnippets(product);
  const imageSrc = product.source.imagePath || product.source.srcPath;

  const handleClick = (): void => {
    if (pageUrn) {
      onNavigate({ pageUrn, dataId, productId: product.id });
    }
  };

  return (
    <div
      className={`search-hit${pageUrn ? " clickable" : ""}${selected ? " selected" : ""}`}
      onClick={pageUrn ? handleClick : undefined}
      onKeyDown={
        pageUrn
          ? (e: KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                handleClick();
              }
            }
          : undefined
      }
      role={pageUrn ? "button" : undefined}
      tabIndex={pageUrn ? 0 : undefined}
    >
      {title && <div className="search-hit-title">{renderHighlightedHtml(title)}</div>}
      {imageSrc && (
        <img className="search-hit-image" src={imageSrc} alt={product.source.title ?? ""} />
      )}
      {snippets.map((snippet, index) => (
        <div key={index} className="search-hit-snippet">
          {renderHighlightedHtml(snippet)}
        </div>
      ))}
    </div>
  );
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Wraps case-insensitive occurrences of `query` in `<em>`, matching autoComplete snippet markup. */
function highlightMatches(text: string, query: string): ReactNode {
  const trimmed = query.trim();
  if (!text || !trimmed) {
    return text;
  }
  const parts = text.split(new RegExp(`(${escapeRegExp(trimmed)})`, "gi"));
  return parts.map((part, index) =>
    index % 2 === 1 ? <em key={index}>{part}</em> : <Fragment key={index}>{part}</Fragment>,
  );
}

function GlossaryResultItem({
  item,
  query,
  selected,
  onNavigate,
}: {
  item: CsgGlossaryItem;
  query: string;
  selected: boolean;
  onNavigate: (item: CsgGlossaryItem) => void;
}): ReactElement {
  return (
    <div
      className={`search-hit glossary-hit clickable${selected ? " selected" : ""}`}
      onClick={() => onNavigate(item)}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          onNavigate(item);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="search-hit-title glossary-hit-term">
        {highlightMatches(item.term?.text ?? "", query)}
      </div>
      {item.definition?.text && (
        <div className="search-hit-snippet glossary-hit-definition">
          {highlightMatches(item.definition.text, query)}
        </div>
      )}
    </div>
  );
}

function ResultGroupShell({
  title,
  count,
  expanded,
  onToggle,
  children,
}: {
  title: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="search-result-group">
      <button
        type="button"
        className={`search-group-header${expanded ? " expanded" : ""}`}
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <span className="search-group-chevron" aria-hidden="true" />
        <span className="search-group-title">{title}</span>
        <span className="search-group-count">
          {count} {count === 1 ? "result" : "results"}
        </span>
      </button>
      {expanded && <div className="search-group-body">{children}</div>}
    </div>
  );
}

function SearchResultGroup({
  group,
  expanded,
  selectedProductId,
  onToggle,
  onNavigate,
}: {
  group: GroupedResults;
  expanded: boolean;
  selectedProductId: string | null;
  onToggle: () => void;
  onNavigate: (target: SearchNavigateTarget) => void;
}): ReactElement {
  return (
    <ResultGroupShell title={group.key} count={group.count} expanded={expanded} onToggle={onToggle}>
      {group.products.map((product) => (
        <SearchResultItem
          key={product.id}
          product={product}
          selected={product.id === selectedProductId}
          onNavigate={onNavigate}
        />
      ))}
    </ResultGroupShell>
  );
}

export const SearchDrawer = ({
  viewerApi,
  packagePath = "",
  onClose,
}: ISearchDrawer): ReactElement => {
  const [searchContext, setSearchContext] = useState<SearchContext | null>(null);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchResults, setSearchResults] = useState<AutoCompleteResponse | null>(null);
  const [glossaryResults, setGlossaryResults] = useState<CsgGlossaryItem[]>([]);
  const [lastSearchQuery, setLastSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set());
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const suggestTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;

    if (!packagePath) {
      setBootstrapError("Search is unavailable without a package path.");
      setIsBootstrapping(false);
      return;
    }

    setIsBootstrapping(true);
    setBootstrapError(null);

    bootstrapSearchContext(packagePath).then((context) => {
      if (cancelled) {
        return;
      }
      if (!context) {
        setBootstrapError("Search indexing is not available for this package yet.");
      } else {
        setBootstrapError(null);
      }
      setSearchContext(context);
      setIsBootstrapping(false);
    });

    return () => {
      cancelled = true;
    };
  }, [packagePath]);

  useEffect(() => {
    if (packagePath) {
      inputRef.current?.focus();
    }
  }, [packagePath]);

  useEffect(() => {
    return () => {
      if (suggestTimerRef.current) {
        clearTimeout(suggestTimerRef.current);
      }
    };
  }, []);

  const runSearch = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.trim()) {
        return;
      }

      if (!searchContext?.indexId) {
        setSearchError("Search is still initializing. Please try again in a moment.");
        return;
      }

      setShowSuggestions(false);
      setIsSearching(true);
      setSearchError(null);

      const trimmedQuery = searchQuery.trim();
      const [results, glossary] = await Promise.all([
        fetchSearchResults(searchContext, trimmedQuery),
        fetchGlossary(searchContext),
      ]);
      setIsSearching(false);

      const glossaryHits = filterGlossary(glossary, trimmedQuery);
      setGlossaryResults(glossaryHits);
      setLastSearchQuery(trimmedQuery);
      setSelectedProductId(null);

      const groupKeys = glossaryHits.length ? [GLOSSARY_GROUP_KEY] : [];

      if (!results) {
        setSearchError("Search request failed. Please try again.");
        setSearchResults(null);
        setExpandedGroups(new Set(groupKeys));
        return;
      }

      setSearchResults(results);
      const groups = groupResultsForDisplay(results);
      setExpandedGroups(new Set([...groupKeys, ...groups.map((g) => g.key)]));
    },
    [searchContext],
  );

  const handleQueryChange = (value: string): void => {
    setQuery(value);
    setSearchError(null);

    if (suggestTimerRef.current) {
      clearTimeout(suggestTimerRef.current);
    }

    if (!searchContext?.indexId || value.trim().length < MIN_SUGGEST_LENGTH) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    suggestTimerRef.current = setTimeout(async () => {
      const items = await fetchSearchSuggestions(searchContext, value.trim());
      setSuggestions(Array.isArray(items) ? items : []);
      setShowSuggestions(Array.isArray(items) && items.length > 0);
    }, SUGGEST_DEBOUNCE_MS);
  };

  const handleSuggestionSelect = (suggestion: string): void => {
    setQuery(suggestion);
    setSuggestions([]);
    setShowSuggestions(false);
    void runSearch(suggestion);
  };

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    void runSearch(query);
  };

  const handleNavigate = (target: SearchNavigateTarget): void => {
    navigateToSearchResult(viewerApi, target, query);
    if (target.productId) {
      setSelectedProductId(target.productId);
      if (searchResults) {
        const groups = groupResultsForDisplay(searchResults);
        const groupKey = groups.find((group) =>
          group.products.some((product) => product.id === target.productId),
        )?.key;
        if (groupKey) {
          setExpandedGroups((prev) => new Set(prev).add(groupKey));
        }
      }
    }
  };

  const handleGlossaryNavigate = (item: CsgGlossaryItem): void => {
    setSelectedProductId(item.itemid);
    void navigateToGlossaryTerm(viewerApi, packagePath, item, lastSearchQuery);
  };

  const expandAllGroups = (): void => {
    setExpandedGroups(
      new Set([
        ...(glossaryResults.length ? [GLOSSARY_GROUP_KEY] : []),
        ...displayGroups.map((group) => group.key),
      ]),
    );
  };

  const collapseAllGroups = (): void => {
    setExpandedGroups(new Set());
  };

  const toggleGroup = (key: string): void => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const displayGroups = searchResults ? groupResultsForDisplay(searchResults) : [];
  const hasResults = displayGroups.length > 0 || glossaryResults.length > 0;
  const showSearchForm = Boolean(packagePath);
  const searchReady = Boolean(searchContext?.indexId);

  return (
    <div className="drawer search-drawer">
      <div className="header">
        <h3 className="title">Search</h3>
        <button className="close-button" aria-label="Close button" onClick={onClose}>
          <div className="close-icon" />
        </button>
      </div>

      <div className="drawer-body">
        {!showSearchForm && bootstrapError && (
          <div className="search-status search-error">{bootstrapError}</div>
        )}

        {showSearchForm && (
          <Fragment>
            <form className="search-form" onSubmit={handleSubmit}>
              <div className="search-input-wrapper">
                <span className="search-input-icon" aria-hidden="true" />
                <input
                  ref={inputRef}
                  type="search"
                  className="search-input"
                  value={query}
                  placeholder="Search"
                  aria-label="Search"
                  disabled={!searchReady}
                  onChange={(e) => handleQueryChange(e.target.value)}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                />
                {query && (
                  <button
                    type="button"
                    className="search-clear-button"
                    aria-label="Clear search"
                    onClick={() => {
                      setQuery("");
                      setSuggestions([]);
                      setShowSuggestions(false);
                      setSearchResults(null);
                      setGlossaryResults([]);
                      setSearchError(null);
                      setSelectedProductId(null);
                      inputRef.current?.focus();
                    }}
                  >
                    ×
                  </button>
                )}
              </div>

              {showSuggestions && suggestions.length > 0 && (
                <ul className="search-suggestions" role="listbox">
                  {suggestions.map((suggestion) => (
                    <li key={suggestion} role="option">
                      <button
                        type="button"
                        className="search-suggestion-item"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSuggestionSelect(suggestion)}
                      >
                        {suggestion}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </form>

            {isBootstrapping && (
              <div className="search-status">Initializing search in the background…</div>
            )}

            {!isBootstrapping && bootstrapError && (
              <div className="search-status search-error">{bootstrapError}</div>
            )}

            {isSearching && <div className="search-status">Searching…</div>}

            {searchError && <div className="search-status search-error">{searchError}</div>}

            {!isSearching && searchResults && !hasResults && (
              <div className="search-status">No results found.</div>
            )}

            {!isSearching && hasResults && (
              <Fragment>
                <div className="search-tabs" role="tablist">
                  <button
                    type="button"
                    className="search-tab active"
                    role="tab"
                    aria-selected="true"
                  >
                    Book
                  </button>
                </div>

                <div className="search-results-toolbar">
                  <button type="button" className="search-toolbar-link" onClick={expandAllGroups}>
                    Expand All
                  </button>
                  <span className="search-toolbar-separator" aria-hidden="true">
                    |
                  </span>
                  <button type="button" className="search-toolbar-link" onClick={collapseAllGroups}>
                    Collapse All
                  </button>
                </div>

                <div className="search-results">
                  {glossaryResults.length > 0 && (
                    <ResultGroupShell
                      title="Glossary"
                      count={glossaryResults.length}
                      expanded={expandedGroups.has(GLOSSARY_GROUP_KEY)}
                      onToggle={() => toggleGroup(GLOSSARY_GROUP_KEY)}
                    >
                      {glossaryResults.map((item) => (
                        <GlossaryResultItem
                          key={item.itemid}
                          item={item}
                          query={lastSearchQuery}
                          selected={item.itemid === selectedProductId}
                          onNavigate={handleGlossaryNavigate}
                        />
                      ))}
                    </ResultGroupShell>
                  )}
                  {displayGroups.map((group) => (
                    <SearchResultGroup
                      key={group.key}
                      group={group}
                      expanded={expandedGroups.has(group.key)}
                      selectedProductId={selectedProductId}
                      onToggle={() => toggleGroup(group.key)}
                      onNavigate={handleNavigate}
                    />
                  ))}
                </div>
              </Fragment>
            )}
          </Fragment>
        )}
      </div>
    </div>
  );
};
